/**
 * Iter 632 — customer check-in wizard: ALL state + effects + handlers
 * live here (moved verbatim from EngageSessionPanel.jsx). The step/card
 * components under this folder are pure renderers over the returned bag.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { usePendingReviews } from "@/components/engage/usePendingReviews";
import { usePointsBalance } from "@/hooks/usePoints";
import { OPTS, errDetail, rememberSession, useCountdown } from "@/features/customerWizard/helpers";

export function useCustomerWizard({ qrCodeId, paidReturn = false, paidSessionId = null, resumeId = null }) {
  const qc = useQueryClient();
  // Iter-613 — ?resume={session_id} notification deep-link: load that
  // session directly (GET /sessions/{id}) so the wizard lands at the
  // right step without a re-scan. Expired sessions fall back to the
  // normal recover/claim flow.
  const [sessionId, setSessionId] = useState(paidReturn ? paidSessionId : resumeId || null);
  const [pending409, setPending409] = useState(null);
  const [fatal, setFatal] = useState(null); // string message
  const [expired, setExpired] = useState(false);
  const [creating, setCreating] = useState(true);
  const [recovered, setRecovered] = useState(false);
  // Iter-528 — TILL-FIRST: customers can never create a session. The
  // auto-connect POST claims the oldest unclaimed staff sale; a 409
  // "WAITING_FOR_TILL|…" means no sale started yet → friendly waiting
  // card + retry every 5 s.
  const [waiting, setWaiting] = useState(null); // string message | null
  // Iter 694 — camera scans open a fresh tab where the customer is often
  // signed out / token-expired: a 401 claim must show "sign in to
  // connect", NEVER "ask the staff" and never a dead-end error.
  const [needsLogin, setNeedsLogin] = useState(false);
  // Iter-558 — Photo → Share progression (local; the backend never
  // stores the customer's photo until it's submitted as proof).
  const [photo, setPhoto] = useState(null); // {b64, previewUrl} | null
  // Iter-627 — extra visit photos (max 3) + one-tap server collage.
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [collageBusy, setCollageBusy] = useState(false);
  const [skipChosen, setSkipChosen] = useState(false);
  const [proofLocal, setProofLocal] = useState(null); // "verified" | "pending" | null
  // Iter-614 — the till corrected the bill mid-session: show an amber
  // notice and ask the customer to re-confirm the new total.
  const [billCorrected, setBillCorrected] = useState(null); // number | null
  const prevBillRef = useRef(null);
  const skipFired = useRef(false);
  const recoverBusy = useRef(false);
  const recoverAttempts = useRef(0);
  const ackRef = useRef(false); // Iter-550 — sticky acknowledge_pending

  const waitingDetail = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 409 && typeof detail === "string" && detail.startsWith("WAITING_FOR_TILL")) {
      return detail.split("|")[1] || "Ask the staff to start your sale at the till";
    }
    return null;
  };

  // Iter-326 — NEVER dead-end on an expired session. Any 410 (or a GET
  // returning status:"expired") auto-creates a replacement session,
  // carrying over the previous bill amount when one was set. The expired
  // card only remains as a last resort after repeated failures.
  const recoverSession = async () => {
    if (recoverBusy.current) return;
    recoverBusy.current = true;
    if (recoverAttempts.current >= 3) {
      setExpired(true);
      recoverBusy.current = false;
      return;
    }
    recoverAttempts.current += 1;
    try {
      // Iter-528 — NEVER send bill_amount (403 under till-first).
      const { data } = await sharedApi.post(
        "/classifieds/engagement/sessions",
        { qr_code_id: qrCodeId, ...(ackRef.current ? { acknowledge_pending: true } : {}) },
        { ...OPTS, skipAuthRedirect: true },
      );
      setPending409(null);
      setExpired(false);
      setWaiting(null);
      setRecovered(true);
      setSessionId(data.id);
      // Iter-621 — replacement session restarts the pay-choice flow.
      setPayChoice(null);
      pointsOnlyCalled.current = false;
      rememberSession(qrCodeId, data);
      qc.setQueryData(["engage-session", data.id], data);
      toast.success("Your session had expired — a fresh one was started automatically.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const waitMsg = waitingDetail(err);
      if (waitMsg) {
        recoverAttempts.current = 0;
        setSessionId(null);
        setExpired(false);
        setWaiting(waitMsg);
      } else if (status === 409 && detail?.code === "pending_review_exists") setPending409(detail);
      else setExpired(true);
    } finally {
      recoverBusy.current = false;
    }
  };

  // Plain async create (not useMutation): the effect-triggered call must
  // be immune to observer/StrictMode edge cases — a stuck "pending" here
  // dead-ends the whole page.
  // Iter 638 — `silent` claims (the 2 s waiting-for-till retry) must not
  // flash the spinner / disable buttons on every tick.
  const startSession = async (acknowledge, silent = false) => {
    if (!silent) setCreating(true);
    try {
      // Iter-550 — once acknowledged, EVERY subsequent claim poll keeps
      // sending acknowledge_pending (else the 409 prompt loops forever).
      if (acknowledge) ackRef.current = true;
      const body = ackRef.current
        ? { qr_code_id: qrCodeId, acknowledge_pending: true }
        : { qr_code_id: qrCodeId };
      const { data } = await sharedApi.post("/classifieds/engagement/sessions", body, {
        ...OPTS,
        // Iter 696 (694) — a 401 here renders the "sign in to connect"
        // card instead of the global hard-redirect.
        skipAuthRedirect: true,
      });
      setPending409(null);
      setWaiting(null);
      setSessionId(data.id);
      rememberSession(qrCodeId, data);
      qc.setQueryData(["engage-session", data.id], data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const waitMsg = waitingDetail(err);
      if (waitMsg) {
        // Iter-528 — no staff sale yet: wait, don't error.
        setWaiting(waitMsg);
      } else if (status === 401) {
        // Iter 694 — expired/missing token: sign-in-then-claim.
        setWaiting(null);
        setNeedsLogin(true);
      } else if (status === 409 && detail?.code === "pending_review_exists") {
        setPending409(detail);
      } else if (status === 410) {
        setExpired(true);
      } else if (!silent) {
        setFatal(errDetail(err, "Couldn't start your session — please try again."));
      }
    } finally {
      if (!silent) setCreating(false);
    }
  };

  // Kick off the session exactly once on mount (ref guard: StrictMode
  // double-effects must not POST twice — the second call would both
  // duplicate the session and swallow the 409 pending-review prompt).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Iter-319 — Stripe returns to ?paid=1&sid=ses_…: never create a
    // fresh session on the way back, just show the paid state.
    // Iter 775 — ?resume=<session_id> WINS over the store QR: a resume
    // deep-link (e-mail "Finish my review", My visits) must open THAT
    // session, never claim a new till sale (which could 409 into the
    // pending-review prompt — the review-resume loop). If the resumed
    // session turns out expired, the session poll's 410/expired recovery
    // falls back to the normal claim flow.
    if (paidReturn || resumeId) {
      setCreating(false);
      return;
    }
    startSession(false);
  }, [qrCodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter-528 — while waiting for the till, retry the claim.
  // Iter 638 — 5 s → 2 s and SILENT (no spinner flicker per tick), so the
  // customer joins within ~2 s of the till starting the sale.
  useEffect(() => {
    if (!waiting || needsLogin) return undefined;
    const t = setInterval(() => startSession(false, true), 2_000);
    return () => clearInterval(t);
  }, [waiting, needsLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionQ = useQuery({
    queryKey: ["engage-session", sessionId],
    enabled: !!sessionId && !expired,
    // Iter 638 — 4-5 s → 2 s so till-side bill entry/corrections appear
    // within ~2 s (react-query structural sharing keeps re-renders away
    // when nothing visible changed).
    refetchInterval: 2_000,
    retry: false,
    queryFn: async () => {
      try {
        const { data } = await sharedApi.get(`/classifieds/engagement/sessions/${sessionId}`, OPTS);
        // Iter-326 — a stale session comes back 200 with status:"expired":
        // recover exactly like a 410 (unless we're on a paid-return view).
        if (data?.status === "expired" && !paidReturn) {
          recoverSession();
        }
        return data;
      } catch (err) {
        if (err?.response?.status === 410) {
          if (paidReturn) return null;
          recoverSession();
          return null;
        }
        throw err;
      }
    },
  });

  const s = sessionQ.data || null;
  const countdown = useCountdown(s?.expires_at);

  // Iter-562 §5 — keep the persisted store name fresh (some claim
  // responses omit `vendor`; the session poll always includes it).
  useEffect(() => {
    if (sessionId && s?.vendor?.business_name && qrCodeId) rememberSession(qrCodeId, s);
  }, [sessionId, s?.vendor?.business_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter-614 — detect a till-side bill correction via the session poll.
  useEffect(() => {
    const b = s?.bill_amount;
    if (b == null) return;
    if (prevBillRef.current != null && prevBillRef.current !== b) setBillCorrected(b);
    if (s?.bill_confirmed) setBillCorrected(null);
    prevBillRef.current = b;
  }, [s?.bill_amount, s?.bill_confirmed]);

  const isPaid =
    s?.status === "paid" || s?.status === "completed" || s?.paid === true;

  // Iter-627 — server-side collage: main + extras (2–4) become one
  // 1080×1080 shared photo; the previous main moves into the strip.
  const makeCollage = async () => {
    if (!sessionId || !photo?.b64 || collageBusy) return;
    setCollageBusy(true);
    try {
      const photos = [photo.b64, ...extraPhotos.map((p) => p.b64)].filter(Boolean).slice(0, 4);
      const { data } = await sharedApi.post(
        `/classifieds/engagement/sessions/${sessionId}/collage`,
        { photos },
        OPTS,
      );
      setExtraPhotos((prev) => [photo, ...prev].slice(0, 3));
      setPhoto({ b64: data.photo_b64, previewUrl: `data:image/jpeg;base64,${data.photo_b64}` });
      toast.success(`🧩 Collage ready — ${data.count} photos in one.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d.trim() ? d : "Collage failed — please try again.");
    } finally {
      setCollageBusy(false);
    }
  };

  // Iter-528 — the till cancelled this sale: go straight back to the
  // waiting state (the retry loop reclaims the next staff sale).
  useEffect(() => {
    if (s?.status === "cancelled_by_staff" && !isPaid) {
      setSessionId(null);
      // Iter-621 — a fresh sale restarts the pay-choice flow too.
      setPayChoice(null);
      pointsOnlyCalled.current = false;
      setWaiting("The store cancelled that sale — waiting for the till to start a new one.");
    }
  }, [s?.status, isPaid]);
  // Iter-326 — only the explicit `expired` flag (recovery exhausted)
  // dead-ends; local countdown/status expiry is handled by recovery.
  const isExpired = expired && !isPaid;

  const onApiError = (err, fallback) => {
    if (err?.response?.status === 410) {
      recoverSession();
      return;
    }
    toast.error(errDetail(err, fallback));
  };

  // Iter-474 — customer self-entry of the bill removed: bills come from
  // the staff/till only (PUT /sessions/{id}/bill is till-side now).
  const confirmMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post(`/classifieds/engagement/sessions/${sessionId}/confirm-bill`, {}, OPTS)).data,
    // Iter 638 — one round-trip: merge the POST response (status/amounts)
    // straight into the session cache instead of re-fetching it.
    onSuccess: (data) => {
      if (data && typeof data === "object" && (data.status || data.amounts || data.bill_confirmed != null)) {
        qc.setQueryData(["engage-session", sessionId], (old) => ({ ...(old || {}), ...data }));
      } else {
        qc.invalidateQueries({ queryKey: ["engage-session", sessionId] });
      }
    },
    onError: (err) => onApiError(err, "Couldn't confirm the bill"),
  });
  const skipMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post(`/classifieds/engagement/sessions/${sessionId}/skip-review`, {}, OPTS)).data,
    onSuccess: () => {
      toast.success("Review skipped — full price applies this visit.");
      qc.invalidateQueries({ queryKey: ["engage-session", sessionId] });
    },
    onError: (err) => onApiError(err, "Couldn't skip the review"),
  });
  // Iter-637 — SMS "are you doing this?" challenge state.
  const [riskChallenge, setRiskChallenge] = useState(null);
  const [riskCode, setRiskCode] = useState("");
  const lastCheckoutBody = useRef({});
  // Iter 638 — /payment-status is a Stripe round-trip: only poll it once a
  // checkout page has actually been opened (or on the ?paid=1 return).
  const [checkoutStarted, setCheckoutStarted] = useState(paidReturn);
  // Iter 641 — "Opening secure payment…" overlay: true from the Pay tap
  // until Stripe opens (kept true through the redirect navigation).
  const [payOpening, setPayOpening] = useState(false);
  const checkoutMut = useMutation({
    mutationFn: async (body) => {
      lastCheckoutBody.current = body || {};
      return (await sharedApi.post(`/classifieds/engagement/sessions/${sessionId}/checkout`, body || {}, OPTS)).data;
    },
    onMutate: () => setPayOpening(true),
    onSuccess: (data) => {
      if (data?.url) {
        setCheckoutStarted(true);
        // keep payOpening true — the overlay covers the redirect hop.
        window.location.href = data.url;
      } else if (data?.risk_confirm_required) {
        // Iter-637 — suspicious-transaction protection: the backend texted
        // a code to the customer's mobile; payment stays locked until they
        // confirm via /risk-confirm, then checkout is retried.
        setPayOpening(false);
        setRiskChallenge(data);
        setRiskCode("");
      } else {
        setPayOpening(false);
        // Iter 647 — a checkout fully covered by points finalises without
        // a Stripe hop (no url): flip checkoutStarted so payment-status is
        // polled and the paid flip + celebration payload arrive exactly
        // like the Stripe-return path.
        setCheckoutStarted(true);
        qc.invalidateQueries({ queryKey: ["engage-session", sessionId] });
      }
    },
    onError: (err) => {
      setPayOpening(false);
      // Iter-637 — 409 {code:"risk_confirm_required", channel:"none"}:
      // no mobile on the account — show the backend's message.
      const d = err?.response?.data;
      if (err?.response?.status === 409 && (d?.code === "risk_confirm_required" || d?.detail?.code === "risk_confirm_required")) {
        setRiskChallenge({ ...(d?.detail && typeof d.detail === "object" ? d.detail : d), risk_confirm_required: true });
        return;
      }
      onApiError(err, "Couldn't start the payment");
    },
  });

  // Iter-637 — confirm the SMS code, then retry the same checkout.
  const riskConfirmMut = useMutation({
    mutationFn: async () =>
      (
        await sharedApi.post(
          `/classifieds/engagement/sessions/${sessionId}/risk-confirm`,
          { code: riskCode.trim() },
          OPTS,
        )
      ).data,
    onSuccess: () => {
      setRiskChallenge(null);
      setRiskCode("");
      toast.success("Thanks — that's confirmed. Taking you to the payment…");
      checkoutMut.mutate(lastCheckoutBody.current);
    },
    onError: (err) => toast.error(errDetail(err, "That code didn't match — please try again.")),
  });

  // Once a checkout page has been opened, poll payment-status so a Stripe
  // payment completed in another tab flips this page to the paid state.
  // Iter 638 — never before the Pay tap (it's a Stripe API round-trip),
  // and 2 s while a checkout is actually open.
  const payStatusQ = useQuery({
    queryKey: ["engage-pay-status", sessionId],
    enabled: !!sessionId && checkoutStarted && !!s?.bill_confirmed && !isPaid && !isExpired,
    refetchInterval: 2_000,
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get(
        `/classifieds/engagement/sessions/${sessionId}/payment-status`,
        OPTS,
      );
      return data;
    },
  });
  const paidViaPoll =
    payStatusQ.data?.paid === true ||
    ["paid", "succeeded", "complete", "completed"].includes(payStatusQ.data?.status);

  // Iter 775 — `payment_only_pending_review_window` is NEVER plain "paid":
  // payment is complete but the 48h review window is open. Routing it to
  // PaidCard keeps the earn-back Photo → Share flow on screen (PaidCard's
  // reviewOutstanding branch) instead of the pay-again BillSection — and a
  // ?resume= deep-link lands exactly there to finish the review.
  const pendingWindow =
    s?.status === "payment_only_pending_review_window" ||
    s?.session_status === "payment_only_pending_review_window";

  const showPaid = paidReturn || isPaid || paidViaPoll || pendingWindow;

  // Iter-558 — review flow status for the wizard + Pay step gating.
  // NOTE: the backend sets status:"verified" whenever payment is
  // unlocked (including after a skip), and review_state
  // "skipped_deferred" for the 48h-window skip — so "skipped" must win.
  const skipped = skipChosen || /skipped/.test(s?.review_state || "");
  const verified =
    !skipped && (s?.status === "verified" || proofLocal === "verified" || s?.review_state === "verified");
  const pendingVerification =
    !verified &&
    !skipped &&
    // status "needs_review" = proof submitted server-side (survives
    // reloads — review_state stays "required" until it's approved).
    // Iter 695 resume fix — a resumed session that already carries a
    // `post` must land on Till/Pay, never bounce back to Photo.
    (proofLocal === "pending" || s?.review_state === "pending" || s?.status === "needs_review" || !!s?.post);

  // Iter-579 — "skip for 48 hours" fires as soon as the till has entered
  // a bill amount (backend no longer requires bill_confirmed), so the
  // Till step shows the honest FULL price before the customer confirms.
  useEffect(() => {
    if (
      skipChosen &&
      sessionId &&
      s?.bill_amount != null &&
      s?.review_state === "required" &&
      ["open", "needs_review"].includes(s?.status) &&
      !skipFired.current
    ) {
      skipFired.current = true;
      skipMut.mutate();
    }
  }, [skipChosen, sessionId, s?.bill_amount, s?.review_state, s?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter-330 — deadline + potential points for the deferred review come
  // from /my/pending-reviews (workorder-verified live). Matched by
  // session_id; review_deadline on the session itself is the fallback.
  const pendingQ = usePendingReviews(showPaid);
  const pendingEntry =
    (pendingQ.data?.pending_reviews || []).find(
      (r) => r.session_id === sessionId,
    ) || null;

  // Iter-330 — vendor's configured review platforms (only `available`
  // ones are offered). Falls back to the static list on any failure.
  const vendorRef =
    s?.vendor_id || s?.vendor?.id || pendingEntry?.qr_code_id || s?.vendor?.qr_code_id || null;
  const platformsQ = useQuery({
    queryKey: ["engage-vendor-platforms", vendorRef],
    enabled: !!vendorRef && (showPaid || s?.review_state === "required"),
    retry: 0,
    queryFn: async () =>
      (await sharedApi.get(`/classifieds/engagement/vendors/${vendorRef}/platforms`, OPTS)).data,
  });

  // Iter-333/335 — "Pay with points" (NZ wallet ONLY). Platform-funded:
  // never mention any vendor cost in copy. Backend caps redemption at
  // nz_classified_balance AND 50% of the charge — the preview line here
  // only mirrors those rules.
  const [usePoints, setUsePoints] = useState(false);
  const pointsQ = usePointsBalance(!!s && !showPaid && !isExpired);
  const nzPoints = pointsQ.data?.nz_classified_balance || 0;

  // Iter-621 — "Just pay with my points": bought/gifted points (≥50)
  // unlock a no-review path (flat 5%, vendor-funded, no 48h window).
  const purchasedPts = pointsQ.data?.nz_purchased_balance || 0;
  const [payChoice, setPayChoice] = useState(null); // null | "review" | "points"
  const pointsOnlyCalled = useRef(false);
  const pointsOnly = payChoice === "points" || s?.review_state === "points_only";
  const spendablePts = pointsOnly ? purchasedPts : nzPoints;

  useEffect(() => {
    if (payChoice !== "points" || pointsOnlyCalled.current) return;
    if (!sessionId || !["open", "needs_review", "verified"].includes(s?.status)) return;
    pointsOnlyCalled.current = true;
    setUsePoints(true);
    (async () => {
      try {
        await sharedApi.post(`/classifieds/engagement/sessions/${sessionId}/points-only`, {}, OPTS);
        qc.invalidateQueries({ queryKey: ["engage-session", sessionId] });
      } catch (err) {
        pointsOnlyCalled.current = false;
        setPayChoice(null);
        toast.error(errDetail(err, "Couldn't switch to points-only — please try again."));
      }
    })();
  }, [payChoice, sessionId, s?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (s?.review_state === "points_only") setUsePoints(true);
  }, [s?.review_state]);

  const amounts = s?.amounts && typeof s.amounts === "object" ? s.amounts : null;

  // WO 801 §2 — live quote: re-prices the bill whenever the points toggle
  // or the reward-code field changes (debounced ~250 ms) and once when the
  // Pay step opens. The table renders from the quote; session.amounts is
  // the fallback while loading / on error. Never compute fees client-side.
  const [rewardCode, setRewardCode] = useState("");
  const [rewardCodeDebounced, setRewardCodeDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setRewardCodeDebounced(rewardCode.trim()), 250);
    return () => clearTimeout(t);
  }, [rewardCode]);
  const redeemPts = usePoints && spendablePts >= 50 ? spendablePts : 0;
  const quoteQ = useQuery({
    queryKey: ["cust-quote", sessionId, redeemPts, rewardCodeDebounced],
    enabled: !!sessionId && !!s?.bill_confirmed && !pendingVerification && !showPaid && !isExpired,
    retry: 0,
    staleTime: 15 * 1000,
    queryFn: async () => {
      const params = {};
      if (redeemPts) params.redeem_points = redeemPts;
      if (rewardCodeDebounced) params.reward_code = rewardCodeDebounced;
      return (
        await sharedApi.get(`/classifieds/engagement/sessions/${sessionId}/quote`, {
          ...OPTS,
          params,
        })
      ).data;
    },
  });
  const quote = quoteQ.data && typeof quoteQ.data === "object" ? quoteQ.data : null;

  // Iter-558 — derive the wizard step: Photo/Share until the review is
  // handled (proof submitted, verified or skipped — or not required at
  // all), then Till (confirm the bill), then Pay.
  const reviewRequired = s?.review_state === "required";
  const reviewResolved = !reviewRequired || verified || pendingVerification || skipped || pointsOnly || proofLocal != null;
  const wizardStep = !reviewResolved ? (photo ? 3 : 2) : s?.bill_confirmed ? 5 : 4;

  return {
    amounts,
    billCorrected,
    checkoutMut,
    collageBusy,
    confirmMut,
    countdown,
    creating,
    expired,
    extraPhotos,
    fatal,
    needsLogin,
    isExpired,
    isPaid,
    makeCollage,
    nzPoints,
    payChoice,
    payOpening,
    payStatusQ,
    pendingEntry,
    pendingVerification,
    pending409,
    photo,
    platformsQ,
    pointsOnly,
    pointsOnlyCalled,
    purchasedPts,
    qc,
    recoverAttempts,
    recoverSession,
    recovered,
    riskChallenge,
    riskCode,
    riskConfirmMut,
    s,
    sessionId,
    setExpired,
    setExtraPhotos,
    setFatal,
    setPayChoice,
    setPhoto,
    setProofLocal,
    setRiskCode,
    setSessionId,
    setSkipChosen,
    setUsePoints,
    setWaiting,
    showPaid,
    skipChosen,
    skipped,
    spendablePts,
    startSession,
    usePoints,
    quote,
    rewardCode,
    setRewardCode,
    vendorRef,
    verified,
    waiting,
    wizardStep,
    proofLocal,
  };
}
